import { CustomApiError } from "@birdwatch/client";
import type { AnyFormApi } from "@tanstack/react-form";

/**
 * Set a server error on a specific field.
 * Equivalent to RHF's form.setError("fieldName", { message: "..." }).
 *
 * Uses TanStack Form's setFieldMeta to add an onSubmit error that
 * appears in field.state.meta.errors and is displayed by FieldWrapper.
 *
 * @example
 * ```ts
 * catch (error) {
 *   setFieldError(form, "email", "This email is already in use.");
 * }
 * ```
 */
export function setFieldError(
  form: AnyFormApi,
  fieldName: string,
  message: string,
): void {
  form.setFieldMeta(fieldName, (prev: any) => ({
    ...prev,
    isTouched: true,
    errorMap: { ...prev.errorMap, onSubmit: message },
  }));
}

/**
 * Set server errors on multiple fields from a REST v5 error response.
 * Maps `CustomApiError.errors` (Record<string, string[]>) to field errors.
 *
 * Only sets errors on fields that exist in the form (skips unknown fields).
 *
 * @example
 * ```ts
 * import { CustomApiError } from "@birdwatch/client";
 *
 * catch (error) {
 *   if (error instanceof CustomApiError && error.errors) {
 *     setFieldErrors(form, error.errors);
 *   } else {
 *     toast.error({ title: error.message });
 *   }
 * }
 * ```
 */
export function setFieldErrors(
  form: AnyFormApi,
  errors: Record<string, string[]>,
): void {
  for (const [fieldName, messages] of Object.entries(errors)) {
    if (form.getFieldMeta(fieldName)) {
      setFieldError(form, fieldName, messages.join(", "));
    }
  }
}

/** Drop a previously-set server error so a resubmit starts clean. */
export function clearFieldError(form: AnyFormApi, fieldName: string): void {
  form.setFieldMeta(fieldName as never, (prev: any) => ({
    ...prev,
    errorMap: { ...prev.errorMap, onSubmit: undefined },
    errorSourceMap: { ...prev.errorSourceMap, onSubmit: undefined },
  }));
}

/**
 * Flatten a REST v5 `CustomApiError.errors` envelope into one message.
 * Returns undefined for anything else (network error, GraphQL failure, plain
 * Error) so the caller falls back to a toast.
 *
 * Flattening across field names is correct for an inline editor: it submits
 * exactly one field, so every field error the server returns belongs to it.
 */
export function getApiFieldErrorMessage(error: unknown): string | undefined {
  if (!(error instanceof CustomApiError) || !error.errors) return undefined;
  const message = Object.values(error.errors).flat().filter(Boolean).join(", ");
  return message || undefined;
}
